import { z } from 'zod';
import type { Lang } from '@/i18n';
import { requestJson } from './http';

export const Session = z.object({
  authenticated: z.literal(true),
  is_new_account: z.boolean().optional(),
  expires_at: z.string(),
  csrf_token: z.string().min(1),
  linked: z.boolean().optional(),
});
export type AuthSession = z.infer<typeof Session>;
export type CompleteAuth = (session: AuthSession) => Promise<void>;
export type AsyncAuthAction = () => Promise<void>;

const Started = z.object({ ok: z.literal(true), expires_in: z.number().positive() });
const ProviderStart = z.object({
  provider: z.enum(['google', 'telegram']),
  client_id: z.string().min(1),
  flow_token: z.string().min(20),
  nonce: z.string().min(20),
  expires_in: z.number().positive(),
});
export type ProviderStartPayload = z.infer<typeof ProviderStart>;

const TelegramContinuation = z.object({
  authenticated: z.literal(false),
  next: z.literal('verify_email'),
  continuation_token: z.string().min(20),
  expires_in: z.number().positive(),
});
export type TelegramContinuationPayload = z.infer<typeof TelegramContinuation>;

const LinkConfirmation = z.object({
  authenticated: z.literal(false),
  next: z.literal('link_confirmation'),
  result: z.literal('link_confirmation_required'),
  link_token: z.string().min(20),
  expires_in: z.number().positive(),
});
export type LinkConfirmationPayload = z.infer<typeof LinkConfirmation>;

const TelegramLoginResult = z.union([Session, TelegramContinuation]);
const TelegramEmailResult = z.union([Session, LinkConfirmation]);

const LinkStatus = z.object({
  confirmed: z.boolean(),
  eligibility: z.enum(['pending', 'allowed', 'blocked']),
  survivor_account_id: z.number().nullable(),
});

const Phase2Account = z.object({
  account_id: z.number(),
  status: z.string(),
  created_at: z.string(),
  identities: z.object({ telegram: z.boolean(), email: z.boolean() }),
  plan: z.string().nullable(),
  plan_status: z.string(),
  active_until: z.string().nullable(),
  trial: z.object({ active: z.boolean(), used: z.boolean(), ends_at: z.string().nullable() }),
  free_status: z.string().nullable(),
  language: z.enum(['ru', 'en', 'de']),
  paid_access: z.object({ plan: z.string(), active_until: z.string().nullable() }).nullable(),
}).passthrough();
const Phase2Entitlements = z.object({
  plan: z.string().nullable(),
  limits: z.object({ coins: z.number(), ai_daily: z.number() }),
  features: z.record(z.boolean()),
}).passthrough();
const Phase2Feature = z.object({ available: z.boolean() }).passthrough();
const Phase2Settings = z.object({
  language: z.enum(['ru', 'en', 'de']),
  selected_coins: z.array(z.string()),
  watch_only_coins: z.array(z.string()),
}).catchall(z.union([Phase2Feature, z.string(), z.boolean(), z.array(z.string())]));

export type RealAccount = z.infer<typeof Phase2Account>;
export type RealEntitlements = z.infer<typeof Phase2Entitlements>;
export type RealSettings = z.infer<typeof Phase2Settings>;

const post = <T extends z.ZodTypeAny>(path: string, schema: T, body: unknown, csrf?: string) =>
  requestJson(path, { method: 'POST', body, csrf, schema });

export const authApi = {
  // A 401 here is the normal anonymous startup state, not an expired in-app
  // session. Private API calls keep the default expiry notification enabled.
  me: () => requestJson('/api/v1/auth/me', { schema: Session, notifyAuthExpired: false }),
  emailStart: (email: string, language: Lang) => post('/api/v1/auth/start', Started, { email, language }),
  emailFinish: (email: string, code: string) => post('/api/v1/auth/finish', Session, { email, code }),
  googleStart: () => post('/api/v1/auth/google/start', ProviderStart, {}),
  googleFinish: (flowToken: string, idToken: string, language: Lang) =>
    post('/api/v1/auth/google', Session, { flow_token: flowToken, id_token: idToken, language }),
  telegramStart: () => post('/api/v1/auth/telegram/start', ProviderStart, {}),
  telegramFinish: (flowToken: string, idToken: string) =>
    post('/api/v1/auth/telegram', TelegramLoginResult, { flow_token: flowToken, id_token: idToken }),
  telegramEmailStart: (continuationToken: string, email: string, language: Lang) =>
    post('/api/v1/auth/telegram/email/start', Started, { continuation_token: continuationToken, email, language }),
  telegramEmailFinish: (continuationToken: string, email: string, code: string) =>
    post('/api/v1/auth/telegram/email/finish', TelegramEmailResult, { continuation_token: continuationToken, email, code }),
  confirmTelegramEmailLink: (linkToken: string) =>
    post('/api/v1/auth/telegram/email/link/confirm', Session, { link_token: linkToken, confirm: true }),
  logout: (csrf: string) => post('/api/v1/auth/logout', z.object({ ok: z.literal(true) }), {}, csrf),
};

export const linkApi = {
  status: (linkToken: string, csrf: string) =>
    post('/api/v1/account-links/status', LinkStatus, { link_token: linkToken }, csrf),
  confirm: (linkToken: string, csrf: string) =>
    post('/api/v1/account-links/confirm', Session, { link_token: linkToken, confirm: true }, csrf),
};

export const phase2Api = {
  account: () => requestJson('/api/v1/account', { schema: Phase2Account }),
  entitlements: () => requestJson('/api/v1/entitlements', { schema: Phase2Entitlements }),
  settings: () => requestJson('/api/v1/settings', { schema: Phase2Settings }),
};

export const authKeys = {
  me: ['auth', 'me'] as const,
  account: ['phase2', 'account'] as const,
  entitlements: ['phase2', 'entitlements'] as const,
  settings: ['phase2', 'settings'] as const,
};
